import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { BackendFormResponse, Producer } from '../../util/api';
import { agentContext } from '../../contexts/agent';
import { inputResponseContext } from '../../contexts/inputResponse';
import { breakpointContext } from '../../contexts/breakpoint';
import { makeCustomEvent, EVENT } from '../../util/events';
import {
    BackendPlan,
    PRICING,
    formatInOutValues,
    BackendPlanRate,
    enrollPlanHandler,
    keepPlanHandler,
    recommendPlanHandler,
    selectNewPlanHandler,
    getPlanRate,
    getPlanName,
} from '../../util/plans';
import { PAGE } from '../../util/page';
import { BREAKPOINT } from '../../util/breakpoints';
import { formatCurrency } from '../../util/format';
import {
    productTypeTooltipMap,
    metalPlanTooltipMap,
    networkTooltip,
    coinsuranceTooltip,
    primaryCareTooltip,
    specialistTooltip,
    urgentcareTooltip,
    emergencyTooltip,
    individualOutOfPocket,
    familyOutOfPocket,
    familyMemberOutOfPocket,
    deductibleTooltip,
    familyDeductible,
    familyMemberDeductible,
    prescriptionDeductible,
    memberExperienceTooltip,
    medicalCareTooltip,
    planAdministrationTooltip,
    overallStarRatingTooltip,
    easyPricingTooltip,
} from '../../util/tooltips';
import { PRODUCT_TYPE, METALLIC_LEVEL } from '../../util/plans';
import { sharedStyles } from '../../util/shared-styles';

import { TemplateResult } from 'lit';
import { getAnalyticPlanDescription, getHostURL } from '../../util/analytics';
import { choose } from 'lit/directives/choose.js';
import { sourcesToRecommend, SOURCE, isEmbeddedAgentEmailDelivery } from '../../util/source';
import { sourceContext } from '../../contexts/source';
import { deliveryMethodContext } from '../../contexts/deliveryMethod';
import { classMap } from 'lit/directives/class-map.js';
import '../../components/comparemap';
import { readOnlyContext } from '../../contexts/readOnly';
interface Feature {
    label: string | TemplateResult;
    key: string;
}

@customElement('rq-marketplace-compare')
export class MarketplaceCompare extends LitElement {
    @consume({ context: sourceContext })
    source!: SOURCE;

    @consume({ context: deliveryMethodContext })
    deliveryMethod?: string;

    @consume({ context: agentContext })
    agent?: Producer;

    @consume({ context: readOnlyContext, subscribe: true })
    @state()
    readOnly = false;

    @property({ type: Array })
    features: Feature[] = [
        { label: 'Plan type', key: 'productType' },
        { label: 'Network', key: 'header' },
        { label: 'Coinsurance', key: 'coinsuranceText' },
        { label: 'Easy pricing', key: 'pricing' },
        { label: 'Primary care visits', key: 'primaryCare' },
        { label: 'Specialist visits', key: 'specialist' },
        { label: html`Urgent care visits<sup>1</sup>`, key: 'urgentCare' },
        { label: html`Emergency room visits<sup>1</sup>`, key: 'emergencyRoom' },
        { label: 'Health savings account eligible', key: 'hsaEligible' },
        { label: 'Individual out-of-pocket maximum', key: 'individualOutOfPocketMax' },
        { label: 'Family member out-of-pocket maximum', key: 'familyMemberOutOfPocketMax' },
        { label: 'Family out-of-pocket maximum', key: 'familyOutOfPocketMax' },
        { label: 'Individual deductible', key: 'individualDeductible' },
        { label: 'Family member deductible', key: 'familyMemberDeductible' },
        { label: 'Family deductible', key: 'familyDeductible' },
        { label: 'Prescription deductible', key: 'rxDeductible' },
        { label: 'Tier 1 medication', key: 'rxGeneric' },
        { label: 'Tier 2 medication', key: 'rxGenericNonPreferred' },
        { label: 'Tier 3 medication', key: 'rxBrandPreferred' },
        { label: 'Tier 4 medication', key: 'rxSpecialty' },
        { label: 'Tier 5 medication', key: 'rxBrandNonPreferred' },
        { label: 'Metal level', key: 'metalType' },
        { label: 'Member experience rating', key: 'memberExperienceRating' },
        { label: 'Medical care rating', key: 'medicalCareRating' },
        { label: 'Plan administration rating', key: 'planAdministrationRating' },
        { label: 'Overall star rating', key: 'overallQualityRating' },
    ];

    featureLabelTooltipMap: Record<string, string> = {
        productType: '',
        network: networkTooltip,
        coinsuranceText: coinsuranceTooltip,
        pricing: easyPricingTooltip,
        primaryCare: primaryCareTooltip,
        specialist: specialistTooltip,
        urgentCare: urgentcareTooltip,
        emergencyRoom: emergencyTooltip,
        hsaEligible: '',
        individualOutOfPocketMax: individualOutOfPocket,
        familyMemberOutOfPocketMax: familyMemberOutOfPocket,
        familyOutOfPocketMax: familyOutOfPocket,
        individualDeductible: deductibleTooltip,
        familyMemberDeductible: familyMemberDeductible,
        familyDeductible: familyDeductible,
        rxDeductible: prescriptionDeductible,
        rxTier6: '',
        metalType: '',
        memberExperienceRating: memberExperienceTooltip,
        medicalCareRating: medicalCareTooltip,
        planAdministrationRating: planAdministrationTooltip,
        overallQualityRating: overallStarRatingTooltip,
    };

    @consume({ context: breakpointContext, subscribe: true })
    breakpoint!: BREAKPOINT;

    @consume({ context: inputResponseContext, subscribe: true })
    @state()
    inputResponse!: BackendFormResponse;

    static styles = [
        sharedStyles,
        css`
            :host {
                all: unset;
            }
            .table-container {
                overflow-x: auto;
                -webkit-overflow-scrolling: touch;
            }
            .compare-table {
                border-collapse: collapse;
                margin-top: 1rem;
                margin-left: auto;
                margin-right: auto;
                width: 99%;
            }
            .compare-table {
                table-layout: fixed;
            }
            @media (max-width: 768px) {
                .compare-table {
                    table-layout: auto;
                    min-width: 600px;
                }
            }
            .compare-table th,
            .compare-table td {
                border: 1px solid var(--lh-color-neutral-200, #d1d3d4);
                padding: 0.75rem;
                text-align: left;
                color: var(--Neutral-800, #222425) !important;
                font-family: 'Blue Ridge Sans' !important;
                font-size: 18px !important;
                font-weight: 400 !important;
                line-height: 24px !important;
                vertical-align: top;
            }

            .compare-table td:first-child,
            .compare-table th:first-child {
                background-color: var(--lh-color-primary-50, #ebf9ff);
                font-weight: bold;
                width: 25%;
            }
            @media (max-width: 992px) {
                .compare-table th:not(:first-child) {
                    width: 25.33% !important;
                }
            }
            .compare-table th:not(:first-child) {
                width: 33.33%;
                padding: 0;
            }
            .action-buttons {
                display: flex;
                justify-content: flex-end;
                margin-top: 1.5rem;
                gap: 1rem;
            }
            .subscript {
                font-size: 1.2rem;
                color: var(--Neutral-800, #222425);
            }
            .stars {
                color: var(--lh-color-primary-500, #00aeef);
            }
            .min-height-130 {
                min-height: 8.125rem;
            }
            .min-height-100 {
                min-height: 7rem;
            }
            .px-25px {
                padding: 0 25px;
            }
            .justify-content-start {
                display: flex;
                justify-content: flex-start;
            }
            .align-items-4 {
                display: inline-flex;
                align-items: center;
                gap: 4px;
            }
            ul {
                margin-left: -10px;
                list-style-type: disc;
            }

            lh-grid-item {
                padding: 0;
                margin: 0;
                gap: 0;
            }
            .compare-table th {
                height: 100%;
                vertical-align: top;
            }

            .grid-container {
                flex: 1 1 auto;
                display: grid;
                grid-template-rows: minmax(3.5rem, auto) minmax(3rem, auto) minmax(3rem, auto) auto;
                width: 90%;
                gap: 1rem;
                padding: 5%;
            }

            .grid-container > .grid-item:first-child {
                margin-bottom: -1rem !important;
            }
            .grid-container.is-shopper-false {
                width: 84%;
                padding: 8%;
            }
            .compare-table th:not(:first-child) {
                padding: 0;
            }

            .margin-bottom-last-child {
                margin-bottom: -1rem;
            }

            .plan-header-row {
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
            }

            .grid-item {
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                min-height: 0;
            }

            .sticky-buttons {
                display: flex;
                flex-direction: column;
                justify-content: flex-end;
                margin-top: auto;
                width: 100%;
            }
            .header-mobile {
                padding-left: 16px;
                padding-right: 16px;
            }
            .pt-15 {
                padding-top: 15px;
            }
            .pt-52 {
                padding-top: 3.25rem;
            }
            .height-3rem {
                height: 3.125rem;
            }
        `,
    ];

    @property()
    plans!: BackendPlan[];

    connectedCallback(): void {
        super.connectedCallback();
        window.bcncDataLayer = window.bcncDataLayer || {};
        window.bcncDataLayer.page = window.bcncDataLayer.page || {};
        window.bcncDataLayer.page.view_url = `${getHostURL()}/compare`;

        this._resizeHandler = () => this.syncPlanHeaderRowHeights();
        window.addEventListener('resize', this._resizeHandler);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        window.removeEventListener('resize', this._resizeHandler);
    }
    private _resizeHandler: () => void;
    get isMobile() {
        return this.breakpoint === BREAKPOINT.PHONE || this.breakpoint === BREAKPOINT.TABLET;
    }

    get isShopper() {
        return Boolean(this.inputResponse?.context?.toLowerCase() === 'shopper');
    }

    get isProducer() {
        return Boolean(this.agent || this.inputResponse?.context?.toLowerCase() === 'producer');
    }

    private topPickChip(_plan: BackendPlan) {
        // TODO: remove below with top pick including undoing pickOrder deletion
        return nothing;
        // if (plan.pickOrder === undefined || plan.pickOrder < 0) return html``;
        // return html`<div>
        //     <lh-chip variant="filled" color="primary">Top Pick</lh-chip>
        // </div>`;
    }
    private costBreakdown(plan: BackendPlan) {
        const primary = this.inputResponse.applicants.find((a) => a.type === 'Primary');
        const spouse = this.inputResponse.applicants.find((a) => a.type === 'Spouse');
        const children = this.inputResponse.applicants.filter((a) => a.type === 'Child');
        const primaryRate = plan.rates.find((r: BackendPlanRate) => r.applicantIndex === primary?.index);
        const spouseRate = plan.rates.find((r: BackendPlanRate) => r.applicantIndex === spouse?.index);
        const childrenRates = plan.rates.filter((r: BackendPlanRate) =>
            children.some((c) => c.index === r.applicantIndex)
        );
        const subsidy =
            plan.marketplace === 'onExchange' && this.inputResponse?.subsidy?.subsidyAmt
                ? this.inputResponse?.subsidy?.subsidyAmt
                : undefined;

        // adding in-line styles because any classes sent as a description would have to be handled in tooltip css
        const lineItemStyle = 'display: grid; grid-template-columns: 20px 60px auto; align-items: center; gap: 8px;';
        const wrapperStyle = `color: var(--lh-color-neutral-white); display: flex; flex-direction: column; gap: 4px; width: 100%; white-space: nowrap; text-align:left;`;
        const rate = getPlanRate(plan);

        return `<lh-typography type="text" size="xs"><div style="${wrapperStyle}">
            <div style="${lineItemStyle}"><div>+</div><div>${formatCurrency(primaryRate?.applicantRate)}</div><div>You${!this.isShopper ? 'r client' : ''}</div></div>
            ${spouseRate ? `<div style="${lineItemStyle}"><div>+</div><div>${formatCurrency(spouseRate?.applicantRate)}</div><div> Your ${!this.isShopper ? "client's " : ''}spouse/domestic partner</div></div>` : ''}
            ${childrenRates.length ? childrenRates.map((r: BackendPlanRate) => `<div style="${lineItemStyle}"><div>+</div><div>${formatCurrency(r.applicantRate)}</div><div> Your ${!this.isShopper ? "client's " : ''}child</div></div>`).join('') : ''}
            ${subsidy && Number(subsidy) > 0 ? `<div style="${lineItemStyle}"><div>-</div><div>${formatCurrency(subsidy)}</div><div>Your ${!this.isShopper ? "client's " : ''}estimated subsidy</div></div>` : ''}
            <div style="border-bottom: 1px solid var(--lh-color-neutral-white);"></div>
            <div style="${lineItemStyle}"><div></div><div>${formatCurrency(rate)}</div><div> Total</div></div></div>
            <br/>
        <div style="color: var(--lh-color-neutral-white);">Note: There is no charge for children beyond the three oldest under age 21.</div></lh-typography>`;
    }

    private getEnrollButton(plan: BackendPlan) {
        const handler = () => enrollPlanHandler(plan.planId, plan.qhpId, this.inputResponse);
        const analyticTag = `rate-quote:${this.source}:u65:quote-compare:enroll-${getAnalyticPlanDescription(plan)}:button-filled`;
        return html`<lh-button
            analytics-id=${analyticTag}
            icon-position="left"
            icon="note-pencil"
            variant="filled"
            @click=${handler}
            .disabled=${this.readOnly}
            full-width
        >
            Enroll${this.isMobile ? '' : ' in this Plan'}
        </lh-button>`;
    }

    private getRecommendButton(plan: BackendPlan) {
        const handler = () => {
            recommendPlanHandler(plan.planId);
            this.viewPlanResults();
        };
        const analyticTag = `rate-quote:${this.source}:u65:quote-compare:recommend-${getAnalyticPlanDescription(plan)}:button-filled`;
        return html`<lh-button
            icon-position="left"
            analytics-id=${analyticTag}
            icon="seal-check"
            variant="filled"
            @click=${handler}
            .disabled=${this.readOnly}
            full-width
        >
            Recommend${this.isMobile ? '' : ' Plan'}
        </lh-button>`;
    }

    private getKeepPlanButton(plan: BackendPlan) {
        const handler = () => keepPlanHandler(plan.planCode);
        const analyticTag = `rate-quote:${this.source}:u65:quote-compare:keep-${getAnalyticPlanDescription(plan)}:button-filled`;
        return html`<lh-button variant="filled" analytics-id=${analyticTag} @click=${handler} full-width
            >Keep${this.isMobile ? '' : ' Original Plan'}</lh-button
        >`;
    }

    private getSelectNewButton(plan: BackendPlan) {
        const handler = () => selectNewPlanHandler(plan.planCode);
        const analyticTag = `rate-quote:${this.source}:u65:quote-compare:select-new-${getAnalyticPlanDescription(plan)}:button-filled`;

        return html`<lh-button variant="filled" analytics-id=${analyticTag} @click=${handler} full-width
            >Select${this.isMobile ? '' : ' New Plan'}</lh-button
        >`;
    }

    private isOriginalPlan({ planCode }: BackendPlan) {
        return planCode === this.inputResponse.currentPlan;
    }

    private getActionButton(plan: BackendPlan) {
        // When an embedded agent chooses to email the quote to their client, act like SOURCE.EMAIL
        // and always recommend (the embedded-agent quote request isn't flagged as producer context).
        if (isEmbeddedAgentEmailDelivery(this.source, this.deliveryMethod)) {
            return this.getRecommendButton(plan);
        }
        return choose(
            this.source,
            [
                ...sourcesToRecommend.map((s): [SOURCE, () => TemplateResult] => [
                    s,
                    () =>
                        this.inputResponse?.context?.toLowerCase() === 'shopper'
                            ? this.getEnrollButton(plan)
                            : this.getRecommendButton(plan),
                ]),
                [SOURCE.DIRECT_SHOPPER, () => this.getEnrollButton(plan)],
                [SOURCE.PABOL, () => this.getEnrollButton(plan)],
                [SOURCE.EMBEDDED_AGENT, () => this.getEnrollButton(plan)],
                [SOURCE.SALESFORCE, () => this.getEnrollButton(plan)],
                [
                    SOURCE.CHANGE_APP,
                    () => {
                        if (this.isOriginalPlan(plan)) {
                            return this.getKeepPlanButton(plan);
                        }
                        return this.getSelectNewButton(plan);
                    },
                ],
            ],
            () => this.getEnrollButton(plan)
        );
    }
    async firstUpdated() {
        await this.updateComplete;
        this.syncPlanHeaderRowHeights();
    }

    async updated() {
        await this.updateComplete;
        this.syncPlanHeaderRowHeights();
    }

    private syncPlanHeaderRowHeights() {
        const headerRows = this.renderRoot.querySelectorAll('.plan-header-row');
        let maxHeight = 0;

        headerRows.forEach((row) => {
            (row as HTMLElement).style.minHeight = '';
            (row as HTMLElement).style.height = '';
        });

        headerRows.forEach((row) => {
            const height = (row as HTMLElement).offsetHeight;
            if (height > maxHeight) {
                maxHeight = height;
            }
        });

        headerRows.forEach((row) => {
            (row as HTMLElement).style.minHeight = `${maxHeight}px`;
            (row as HTMLElement).style.height = `${maxHeight}px`;
        });
    }
    private renderPlanHeader(plan: BackendPlan) {
        const rate = getPlanRate(plan);
        const name = getPlanName(plan);
        return html`<th>
            <div class="grid-container${!this.isShopper ? ' is-shopper-false' : ''}">
                <div class="grid-item plan-header-row">
                    <lh-typography>${this.topPickChip(plan)}</lh-typography>
                    <lh-typography selector="p" size=${this.isMobile && !this.isShopper ? 'xs' : 'lg'}
                        ><strong>${name}</strong></lh-typography
                    >
                </div>

                ${this.isShopper
                    ? nothing
                    : html`
                          <div class="grid-item pt-15">
                              <rq-comparemap .plan=${plan}></rq-comparemap>
                          </div>
                      `}
                <div class="grid-item ${!this.isMobile ? 'pt-52' : ''} ${this.isShopper ? 'pt-15' : ''} height-3rem">
                    <rq-tooltip
                        border-color="var(--lh-color-primary-800)"
                        .description=${this.costBreakdown(plan)}
                        position=${this.isMobile ? 'top' : 'left'}
                    >
                        <lh-typography type="heading" selector="h5" size="xxl">
                            ${formatCurrency(rate)}<span class="subscript">/mo</span>
                        </lh-typography>
                    </rq-tooltip>
                </div>

                <div class="grid-item sticky-buttons ${this.isShopper ? 'margin-bottom-last-child' : ''}">
                    <lh-button-group orientation="vertical" alignment="auto">
                        <lh-button
                            icon-position="left"
                            variant="outlined"
                            icon="scroll"
                            full-width
                            @click=${() => this.viewPlanDetails(plan.planId)}
                            analytics-id="rate-quote:${this
                                .source}:u65:quote-compare:details-${getAnalyticPlanDescription(plan)}:button-outlined"
                        >
                            ${this.isMobile ? 'Details' : 'View Plan Details'}
                        </lh-button>
                        ${this.getActionButton(plan)}
                    </lh-button-group>
                </div>
            </div>
        </th>`;
    }

    private renderFeatureRow(feature: Feature) {
        return html`<tr>
            <td>
                <lh-typography selector="p" size=${this.isMobile ? 'xs' : 'md'}>
                    <span class="align-items-4">
                        ${feature.key === 'pricing' ? html`<lh-icon name="tag"></lh-icon>` : ''}
                        ${this.featureLabelTooltipMap[feature.key]
                            ? html`
                                  ${feature.key === 'pricing'
                                      ? html`<div class="justify-content-start">
                                            <rq-tooltip .description=${this.featureLabelTooltipMap[feature.key]}>
                                                <strong>${feature.label}</strong>
                                            </rq-tooltip>
                                        </div>`
                                      : html`<lh-tooltip .description=${this.featureLabelTooltipMap[feature.key]}>
                                            <strong>${feature.label}</strong>
                                        </lh-tooltip>`}
                              `
                            : html`<strong>${feature.label}</strong>`}
                    </span>
                </lh-typography>
            </td>
            ${this.plans.map((plan) => this.renderFeatureCell(feature, plan))}
        </tr>`;
    }

    private renderFeatureCell(feature: Feature, plan: BackendPlan) {
        const value = plan[feature.key as keyof BackendPlan];

        const splitFeatures = [
            'primaryCare',
            'specialist',
            'rxGeneric',
            'rxGenericNonPreferred',
            'rxBrandPreferred',
            'rxSpecialty',
            'rxBrandNonPreferred',
            'urgentCare',
            'preventiveCare',
            'coinsuranceText',
            'outOfPocketMaxIndividual',
            'outOfPocketMaxFamilyMember',
            'outOfPocketMaxFamily',
            'individualDeductible',
            'familyMemberDeductible',
            'familyDeductible',
        ];

        if (splitFeatures.includes(feature.key)) {
            const { inNetwork, outOfNetwork } = formatInOutValues(plan, feature.key);

            if (inNetwork === "Doesn't Apply" && outOfNetwork === "Doesn't Apply") {
                return html`
                    <td>
                        <lh-typography selector="p">Doesn't Apply</lh-typography>
                    </td>
                `;
            }

            return html`
                <td>
                    <lh-typography selector="p">
                        ${inNetwork === 'N/A' && outOfNetwork === 'N/A'
                            ? "Doesn't Apply"
                            : html`
                                  ${inNetwork === 'N/A' ? 'Not Covered in-network' : `${inNetwork} in-network`}<br />
                                  ${outOfNetwork === 'N/A'
                                      ? 'Not Covered out-of-network'
                                      : `${outOfNetwork} out-of-network`}
                              `}
                    </lh-typography>
                </td>
            `;
        }

        // Handle ratings dynamically
        if (
            feature.key === 'memberExperienceRating' ||
            feature.key === 'medicalCareRating' ||
            feature.key === 'planAdministrationRating' ||
            feature.key === 'overallQualityRating'
        ) {
            // Were asked to differentiate these plans by adding a "New Plan" label (May 2025)
            const newPlans = ['11512NC0430001', '11512NC0430002', '11512NC0430003'];
            // const effectiveYear = this.inputResponse?.effectiveStart?.split('/')?.pop();
            const isNewPlan = newPlans.some((newPlan) => plan.bolCode?.includes(newPlan));
            const isNewHMOPlan = plan.productType.includes(PRODUCT_TYPE.HMO);
            return html`<td>
                <lh-typography selector="p">
                    ${value === 0 || value === undefined || value === '0.0'
                        ? `${isNewPlan || isNewHMOPlan ? 'New Plan - ' : ''}Not Rated`
                        : Array.from(
                              { length: 5 },
                              (_, i) => html`
                                  <span class="stars"
                                      ><lh-icon
                                          size="xs"
                                          name="${i < Number(value) ? 'star-fill' : 'star'}"
                                          class="star-icon"
                                      ></lh-icon>
                                  </span>
                              `
                          )}
                </lh-typography>
            </td>`;
        }

        // Handle metal level with tooltip
        if (feature.key === 'metalType') {
            const tooltip = metalPlanTooltipMap.get(plan.metal as METALLIC_LEVEL);
            return html`<td>
                <lh-typography selector="p">
                    ${tooltip ? html`<lh-tooltip .description=${tooltip}>${plan.metal}</lh-tooltip>` : plan.metal}
                </lh-typography>
            </td>`;
        }

        // Handle Easy Pricing
        if (feature.key === 'pricing') {
            const isEasyPricing = plan.pricing === PRICING.EASY;
            return html`<td>
                <lh-typography selector="p">${isEasyPricing ? 'Yes' : 'No'}</lh-typography>
            </td>`;
        }

        if (feature.key === 'productType') {
            // Handle product type with tooltip
            return html`<td>
                <lh-typography selector="p">
                    <lh-tooltip .description=${productTypeTooltipMap.get(plan.productType as PRODUCT_TYPE)}>
                        ${value}
                    </lh-tooltip>
                </lh-typography>
            </td>`;
        }

        // Handle urgent care and emergency room with superscript
        if (feature.key === 'urgentCare' || feature.key === 'emergencyRoom') {
            return html`<td>
                <lh-typography>${value ? html`${value}` : 'Not Covered'}</lh-typography>
            </td>`;
        }

        // Default case for other features
        return html`<td><lh-typography>${value ?? 'Not Covered'}</lh-typography></td>`;
    }

    private viewPlanDetails(planId: string) {
        this.dispatchEvent(makeCustomEvent(EVENT.CHANGE_PAGE, { page: PAGE.DETAILS, planId }));
    }

    private viewPlanResults() {
        this.dispatchEvent(makeCustomEvent(EVENT.CHANGE_PAGE, { page: PAGE.RESULTS }));
    }

    render() {
        // Determine the correct year for the info text
        const effectiveYear = this.inputResponse?.effectiveStart?.split('/')?.pop();
        const surveyYear = effectiveYear === '2026' ? '2025' : '2026';
        return html`
            <lh-container
                theme="white"
                content-width="xxl"
                margin-top="md"
                margin-bottom="lg"
                padding-top="md"
                padding-bottom="lg"
                gutters=${this.isMobile ? 'none' : 'sm'}
            >
                <div class="px-25px">
                    <div class=${classMap({ 'header-mobile': this.isMobile })}>
                        <div class="header">
                            <lh-button
                                variant="text"
                                icon="caret-left"
                                icon-position="left"
                                @click=${this.viewPlanResults}
                                analytics-id="rate-quote:${this.source}:u65:quote-compare:back:button-text"
                            >
                                Back to Plan Options
                            </lh-button>
                            <lh-typography type="heading" selector="h5" size="xl">Compare Plans</lh-typography>
                        </div>

                        <lh-typography selector="p" size="xs">
                            To help make the best decision, ask yourself the following questions:
                            <ul>
                                <li>What benefits will I use most often?</li>
                                <li>What costs are most important to my family?</li>
                                <li>Are there any unexpected costs that I should consider?</li>
                            </ul>
                        </lh-typography>
                    </div>
                    <!-- Add a scrollable container -->
                    <div class="table-container">
                        <table class="compare-table">
                            <tbody>
                                <tr>
                                    <td class="align-middle">
                                        <lh-typography selector="p" size=${this.isMobile ? 'xs' : 'md'}
                                            ><strong>Plan name</strong></lh-typography
                                        >
                                    </td>
                                    ${this.plans.map((plan) => this.renderPlanHeader(plan))}
                                </tr>
                            </tbody>
                            <tbody>
                                ${this.features.map((feature) => this.renderFeatureRow(feature))}
                            </tbody>
                        </table>
                    </div>
                    <!-- Plans Compare Info Text -->
                    <lh-container padding-top="md" content-width="xxl" alignment="left" gutters="sm">
                        <lh-typography size="sm">
                            Plan quality ratings and enrollee survey results are calculated by CMS using data provided
                            by health plans in ${surveyYear}. The ratings are being displayed for health plans for the
                            ${effectiveYear} plan year. Learn more about these ratings.
                            <a href="https://www.healthcare.gov/quality-ratings/" target="_blank"
                                >https://www.healthcare.gov/quality-ratings/<lh-icon
                                    size="sm"
                                    name="arrow-square-out"
                                    class="pdfColor"
                                ></lh-icon
                            ></a>
                        </lh-typography>
                    </lh-container>
                </div>
            </lh-container>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rq-marketplace-compare': MarketplaceCompare;
    }
}
